import { Badge, Button, IconButton } from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import {
  Drawer,
  EllipsisMenuCopyItem,
  EllipsisMenuDangerItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import { type Maybe, day, formatCurrency, utc } from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  Tooltip,
  rem,
} from '@mantine/core';
import { formatPercentage } from '@vrplatform/ui-common';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { HiddenFeatureIndicator } from '../../../../components';
import {
  DrawerCollapsableTable,
  DrawerHeader,
} from '../../../../drawers/_components';
import { ListingInfoCard } from '../../../listings/_components';
import { ListingEllipsisMenuModals } from '../../../listings/_components/ListingEllipsisMenuModals';
import { FEE_TYPES } from '../_components';
import { useFeesOverviewRouteParams } from '../_hooks';
import type { FeeOverviewRoute } from '../_types';
import {
  ListingFeeEllipsisMenuModals,
  useListingsSingleFeeTableEllipsisMenuModalsState,
} from './ListingFeeEllipsisMenuModals';
import { useListingFeeDrawer } from './useListingFeeDrawer';
import {
  type ListingFeeDetail,
  useListingFeeDrawerQuery,
} from './useListingFeeDrawerQuery';

export const ListingFeeDrawer = () => {
  const { opened, close, listingId } = useListingFeeDrawer();
  const [{ isGL: GL }] = useTeam();

  const goto = useNavigate();

  const { isLoading, data } = useListingFeeDrawerQuery(listingId);
  const listing = data?.listing;

  const [modalOpened, setModalOpened] = useState<
    'deactivate' | 'opening-balance' | null
  >(null);

  const closeModal = () => setModalOpened(null);

  const showMenuItems = !!listing;

  const isDeletedInPms = listing?.sourceStatus === 'deleted';

  return (
    <Drawer opened={opened} onClose={close} size={550}>
      <DrawerHeader
        closeDrawer={close}
        title={
          <Group wrap="nowrap" gap={rem(8)}>
            {isDeletedInPms && (
              <Tooltip label="This listing was deleted in your PMS">
                <Icon
                  icon="AlertTriangleIcon"
                  color={(theme) => theme.colors.orange[7]}
                  size={20}
                />
              </Tooltip>
            )}
            <Title order={3} display="block">
              {listing?.name || 'Missing name'}
            </Title>
          </Group>
        }
        type="Listing"
        loading={isLoading}
      >
        {showMenuItems && GL && (
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              goto(`/listing/${listing.id}`);
            }}
            variant="outline"
            icon="ArrowRightCircleIcon"
            tooltip="Go to detail"
          />
        )}
      </DrawerHeader>
      {!listing && !isLoading
        ? 'No listing found'
        : listingId && (
            <ListingDetail
              listing={listing}
              isLoading={isLoading}
              listingId={listingId}
            />
          )}
      <ListingEllipsisMenuModals
        listing={listing || null}
        // deleteModal={{
        //   opened: modalOpened === 'delete',
        //   closeModal,
        // }}
        deactivationModal={{
          opened: modalOpened === 'deactivate',
          closeModal,
        }}
        openingBalanceModal={{
          opened: modalOpened === 'opening-balance',
          closeModal,
        }}
      />
    </Drawer>
  );
};

const ListingDetail = ({
  listing,
  isLoading,
  listingId,
}: {
  listing: Maybe<ListingFeeDetail>;
  isLoading: boolean;
  listingId: string;
}) => {
  const { feeType } = useFeesOverviewRouteParams();

  return (
    <ListingDrawerFeeSections
      feeType={feeType}
      feeSubscriptions={listing?.feeSubscriptions}
      isLoading={isLoading}
      listingId={listingId}
    >
      {listing && (
        <ListingInfoCard
          {...listing}
          currentBusinessModel={listing.activeOwnership?.businessModel}
          disabledAutomations={[]}
          formattedAddress={listing.address.full}
        />
      )}
    </ListingDrawerFeeSections>
  );
};

export const ListingDrawerFeeSections = ({
  feeSubscriptions,
  isLoading,
  children,
  listingId,
  feeType,
}: {
  feeSubscriptions: Maybe<ListingFeeDetail['feeSubscriptions']>;
  isLoading: boolean;
  children?: ReactNode;
  listingId: string;
  feeType: FeeOverviewRoute;
}) => {
  const { closeModal, opened, setOpened } =
    useListingsSingleFeeTableEllipsisMenuModalsState();

  const current = useMemo(
    () => feeSubscriptions?.filter((sub) => sub.type === 'current') ?? [],
    [feeSubscriptions]
  );
  const future = useMemo(
    () => feeSubscriptions?.filter((sub) => sub.type === 'future') ?? [],
    [feeSubscriptions]
  );
  const past = useMemo(
    () => feeSubscriptions?.filter((sub) => sub.type === 'past') ?? [],
    [feeSubscriptions]
  );

  if (!feeSubscriptions) return null;

  const isSingle = feeType === 'commissions' || feeType === 'cleaning-fees';

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      {children}
      {isSingle ? (
        <SingleFeeSection
          listingId={listingId}
          feeSubscriptions={feeSubscriptions}
          openModal={setOpened}
          title={FEE_TYPES[feeType].title}
          feeType={feeType}
        />
      ) : (
        <>
          {/* Current */}
          <SingleFeeSection
            title={'Current'}
            listingId={listingId}
            feeSubscriptions={current}
            openModal={setOpened}
            defaultOpened={true}
            feeType={feeType}
          />

          {/* Future */}
          <SingleFeeSection
            title={'Future'}
            listingId={listingId}
            feeSubscriptions={future}
            openModal={setOpened}
            defaultOpened={future.length > 0}
            feeType={feeType}
          />

          {/* Past */}
          <SingleFeeSection
            title={'Past'}
            listingId={listingId}
            feeSubscriptions={past}
            openModal={setOpened}
            defaultOpened={future.length > 0}
            feeType={feeType}
          />
        </>
      )}

      <ListingFeeEllipsisMenuModals
        feeType={feeType}
        deleteModal={{
          closeModal,
          opened: opened?.modal === 'delete',
        }}
        editModal={{
          closeModal,
          opened: opened?.modal === 'edit',
        }}
        addUpcomingModal={{
          closeModal,
          opened: opened?.modal === 'add-upcoming',
        }}
        subscription={opened?.subscription}
        listingId={opened?.listingId}
      />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

const SingleFeeSection = ({
  listingId,
  feeSubscriptions,
  openModal,
  title,
  defaultOpened = true,
  feeType,
}: {
  listingId: string;
  title: string;
  feeSubscriptions: ListingFeeDetail['feeSubscriptions'];
  openModal: ReturnType<
    typeof useListingsSingleFeeTableEllipsisMenuModalsState
  >['setOpened'];
  defaultOpened?: boolean;
  feeType: FeeOverviewRoute;
}) => {
  const columns = useFeeSectionColumns();

  const addFee = useCallback(() => {
    openModal({
      listingId,
      modal: 'add-upcoming',
      subscription: null,
    });
  }, [openModal, listingId]);

  return (
    <DrawerCollapsableTable
      title={title}
      rightSection={<IconButton icon="PlusIcon" onClick={addFee} />}
      rowData={feeSubscriptions}
      columns={columns}
      defaultOpened={defaultOpened}
      emptyRowsFallback={() => (
        <Center
          sx={{
            flexDirection: 'column',
          }}
        >
          <Icon
            icon={FEE_TYPES[feeType].icon}
            color={(theme) => theme.colors[theme.primaryColor][6]}
          />
          <Button
            variant="transparent"
            leftIcon="PlusIcon"
            color="primary"
            onClick={addFee}
            sx={(theme) => ({
              marginTop: theme.spacing.xs,
            })}
          >
            Add {FEE_TYPES[feeType].title.slice(0, -1)}
          </Button>
        </Center>
      )}
      rowMenu={{
        menuItems: ({ row }) => {
          const openEditModal = () =>
            openModal({
              modal: 'edit',
              subscription: row.original,
              listingId,
            });

          const openDeleteModal = () =>
            openModal({
              modal: 'delete',
              subscription: row.original,
              listingId,
            });

          return (
            <>
              <EllipsisMenuItem
                onClick={openEditModal}
                customIcon={<Icon icon="Edit3Icon" size={16} />}
              >
                Update
              </EllipsisMenuItem>

              <EllipsisMenuDivider />

              <EllipsisMenuDangerItem onClick={openDeleteModal}>
                Delete
              </EllipsisMenuDangerItem>

              <HiddenFeatureIndicator permission="super-admin">
                <EllipsisMenuCopyItem value={row.original.id} />
              </HiddenFeatureIndicator>
            </>
          );
        },
      }}
    />
  );
};

const useFeeSectionColumns = () =>
  useMemo<MRT_ColumnDef<ListingFeeDetail['feeSubscriptions'][number]>[]>(
    () => [
      {
        header: 'Owner',
        accessorKey: 'id',
        Cell: ({ row }) => {
          const sub = row.original;

          const today = day();

          const isActive =
            today.isAfter(sub.startAt) &&
            (!sub.endAt || today.isBefore(sub.endAt));

          return (
            <Box>
              <Group gap={5} mb={2}>
                <Text>{sub.recurringFee.name}</Text>
                {isActive && (
                  <Badge size="xs" color="green">
                    Active
                  </Badge>
                )}
              </Group>
              <Group wrap="nowrap" gap={5}>
                <Text size="xs" c="gray">
                  {utc(sub.startAt).format('LL')}
                </Text>
                <Icon
                  icon="ArrowRightIcon"
                  size={12}
                  strokeWidth={2}
                  color={(theme) => theme.colors.neutral[4]}
                />
                <Text c={'gray'} size="xs">
                  {sub.endAt ? utc(sub.endAt).format('LL') : 'forever'}
                </Text>
              </Group>
            </Box>
          );
        },
      },
      {
        header: 'Rate',
        accessorKey: 'rate',
        minSize: 70,
        size: 70,
        maxSize: 0,
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableHeadCellProps: {
          align: 'right',
        },
        Cell: ({ row }) => {
          const rate =
            row.original.rate ??
            (row.original.recurringFee as any)?.defaultRate ??
            0;

          if (row.original.recurringFee.rateType === 'flat') {
            return formatCurrency(rate / 100);
          }

          return `${formatPercentage(rate, 'super-base-points')}`;
        },
      },
    ],
    []
  );
